import Phaser from 'phaser';
import {
  ATTACKER_SIZE,
  FONT_FAMILY,
} from '../config/constants';
import { getGameplayColorHex } from '../config/colors';
import { ConveyorSystem } from '../systems/ConveyorSystem';
import { crispText } from '../ui/textUtils';
import { soundManager } from '../audio/SoundManager';

/**
 * Owns attacker sprite creation, per-frame position updates,
 * ammo label updates, and fade-out / exhaustion animations.
 *
 * GameScene delegates attacker visual management to this class.
 */

/**
 * Counter-rotate all Text children in a container so labels stay upright
 * while the container itself is rotated on the conveyor.
 */
export function counterRotateLabels(container: Phaser.GameObjects.Container, angle: number): void {
  const negAngle = -angle;
  container.each((child: Phaser.GameObjects.GameObject) => {
    if (child instanceof Phaser.GameObjects.Text) {
      (child as Phaser.GameObjects.Text).setRotation(negAngle);
    }
  });
}

/** Typed references to children within an attacker container, avoiding unsafe getAt() casts. */
interface AttackerParts {
  bodies: Phaser.GameObjects.Image[];
  labels: Phaser.GameObjects.Text[];
}

export class AttackerRenderer {
  private scene: Phaser.Scene;
  private conveyor: ConveyorSystem;

  /** Attacker sprites: attackerId -> Container (glow + body + label) */
  readonly attackerSprites = new Map<number, Phaser.GameObjects.Container>();

  /** Attackers mid-transition -- skip conveyor position updates during entry lerp */
  readonly transitioningAttackers = new Set<number>();

  /**
   * Cleanup data for multi-orb staggered dispatch animations (keyed by attacker ID).
   * Allows force-cleanup if the attacker exhausts before the animation completes.
   */
  readonly transitionCleanup = new Map<number, {
    orbSprites: Phaser.GameObjects.Container[];
    connector: Phaser.GameObjects.Graphics;
    updateHandler: () => void;
    tweens: Phaser.Tweens.Tween[];
    delayedCalls: Phaser.Time.TimerEvent[];
  }>();

  /** Typed child references for each container, avoiding unsafe getAt() casts. */
  private readonly containerParts = new Map<Phaser.GameObjects.Container, AttackerParts>();

  constructor(scene: Phaser.Scene, conveyor: ConveyorSystem) {
    this.scene = scene;
    this.conveyor = conveyor;
  }

  /** Reset mutable state (called from GameScene.init). */
  reset(): void {
    this.attackerSprites.clear();
    this.transitioningAttackers.clear();
    this.transitionCleanup.clear();
    this.containerParts.clear();
  }

  // ── Visual creation ────────────────────────────────────────────────

  createAttackerVisual(x: number, y: number, orbs: { colorIndex: number; ammo: number; mystery?: boolean }[]): Phaser.GameObjects.Container {
    const children: Phaser.GameObjects.GameObject[] = [];
    const bodies: Phaser.GameObjects.Image[] = [];
    const labels: Phaser.GameObjects.Text[] = [];
    const size = orbs.length;

    if (size === 1) {
      const isMystery = orbs[0].mystery ?? false;
      const textureKey = isMystery ? 'mystery' : `${orbs[0].colorIndex}`;

      // Glow halo behind
      const glow = this.scene.add.image(0, 0, `glow-${textureKey}`);
      glow.setDisplaySize(ATTACKER_SIZE * 1.8, ATTACKER_SIZE * 1.8);
      glow.setAlpha(0.6);
      glow.setBlendMode(Phaser.BlendModes.ADD);
      children.push(glow);

      // Main body
      const body = this.scene.add.image(0, 0, `attacker-${textureKey}`);
      body.setDisplaySize(ATTACKER_SIZE, ATTACKER_SIZE);
      if (orbs[0].ammo <= 0) body.setAlpha(0.35);
      children.push(body);
      bodies.push(body);

      // Ammo label (show "?" for mystery)
      const labelText = isMystery ? '?' : `${orbs[0].ammo}`;
      const label = crispText(this.scene.add.text(0, 0, labelText, {
        fontFamily: FONT_FAMILY,
        fontSize: '30px',
        color: isMystery ? '#ccccdd' : (orbs[0].ammo <= 0 ? '#888888' : '#ffffff'),
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4,
      })).setOrigin(0.5);
      children.push(label);
      labels.push(label);
    } else {
      // Multi-size: linked orbs laid out along travel direction (local Y-axis)
      const spacing = ATTACKER_SIZE * 0.65;
      const totalLength = spacing * (size - 1);
      const startOff = -totalLength / 2;

      // Single elongated glow (use mystery glow if first orb is mystery)
      const firstMystery = orbs[0].mystery ?? false;
      const glowKey = firstMystery ? 'mystery' : `${orbs[0].colorIndex}`;
      const glow = this.scene.add.image(0, 0, `glow-${glowKey}`);
      glow.setDisplaySize(ATTACKER_SIZE * 1.8, totalLength + ATTACKER_SIZE * 1.8);
      glow.setAlpha(0.5);
      glow.setBlendMode(Phaser.BlendModes.ADD);
      children.push(glow);

      for (let i = 0; i < size; i++) {
        const cy = startOff + i * spacing;
        const isMystery = orbs[i].mystery ?? false;
        const textureKey = isMystery ? 'mystery' : `${orbs[i].colorIndex}`;

        const body = this.scene.add.image(0, cy, `attacker-${textureKey}`);
        body.setDisplaySize(ATTACKER_SIZE, ATTACKER_SIZE);
        if (orbs[i].ammo <= 0) body.setAlpha(0.35);
        children.push(body);
        bodies.push(body);

        // Per-orb ammo label
        const labelText = isMystery ? '?' : `${orbs[i].ammo}`;
        const label = crispText(this.scene.add.text(0, cy, labelText, {
          fontFamily: FONT_FAMILY,
          fontSize: '26px',
          color: isMystery ? '#ccccdd' : (orbs[i].ammo <= 0 ? '#888888' : '#ffffff'),
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 4,
        })).setOrigin(0.5);
        children.push(label);
        labels.push(label);
      }
    }

    const container = this.scene.add.container(x, y, children);
    this.containerParts.set(container, { bodies, labels });
    return container;
  }

  // ── Per-frame updates ──────────────────────────────────────────────

  updateAttackerSpritePositions(): void {
    const positions = this.conveyor.getAttackerPositions();
    for (const entry of positions) {
      // Skip attackers mid-transition lerp -- their position is tween-driven
      if (this.transitioningAttackers.has(entry.attackerId)) continue;
      const sprite = this.attackerSprites.get(entry.attackerId);
      if (sprite) {
        const pos = this.conveyor.getPositionAtT(entry.t);
        const angle = this.conveyor.getAngleAtT(entry.t);
        sprite.setPosition(pos.x, pos.y);
        sprite.setRotation(angle);
        // Counter-rotate text labels so they stay upright
        counterRotateLabels(sprite, angle);
      }
    }
  }

  updateAttackerAmmoLabel(attackerId: number, orbs: { ammo: number; mystery?: boolean }[]): void {
    const container = this.attackerSprites.get(attackerId);
    if (!container || container.length < 2) return;

    const parts = this.containerParts.get(container);
    if (!parts) return;

    for (let i = 0; i < orbs.length; i++) {
      const isMystery = orbs[i].mystery ?? false;
      const body = parts.bodies[i];
      if (body && body.setAlpha) body.setAlpha(orbs[i].ammo <= 0 ? 0.35 : 1);
      const label = parts.labels[i];
      if (label && label.setText) {
        label.setText(isMystery ? '?' : `${orbs[i].ammo}`);
        label.setColor(isMystery ? '#ccccdd' : (orbs[i].ammo <= 0 ? '#888888' : '#ffffff'));
      }
    }
  }

  // ── Fade / removal animations ──────────────────────────────────────

  fadeAttackerSprite(attackerId: number): void {
    // Force-cleanup any in-flight multi-orb staggered animation
    this.cleanupTransitionAnimation(attackerId);

    const sprite = this.attackerSprites.get(attackerId);
    if (!sprite) return;
    this.attackerSprites.delete(attackerId);
    const parts = this.containerParts;
    this.scene.tweens.add({
      targets: sprite,
      alpha: 0,
      scaleX: 0.5,
      scaleY: 0.5,
      duration: 250,
      ease: 'Power2',
      onComplete: () => {
        parts.delete(sprite);
        sprite.destroy();
      },
    });
  }

  /** Enhanced fade for ammo-depleted attackers: slower, drifts up, shrinks more, particle burst. */
  fadeExhaustedAttackerSprite(attackerId: number, colorIndex: number): void {
    // Force-cleanup any in-flight multi-orb staggered animation
    this.cleanupTransitionAnimation(attackerId);

    const sprite = this.attackerSprites.get(attackerId);
    if (!sprite) return;
    this.attackerSprites.delete(attackerId);
    const parts = this.containerParts;
    soundManager.attackerExhausted();

    this.scene.tweens.add({
      targets: sprite,
      alpha: 0,
      scaleX: 0.3,
      scaleY: 0.3,
      y: sprite.y - 24,
      duration: 550,
      ease: 'Power2',
      onComplete: () => {
        // Color-matched particle burst on disappearance
        const color = getGameplayColorHex(colorIndex);
        const emitter = this.scene.add.particles(sprite.x, sprite.y, 'particle', {
          speed: { min: 30, max: 80 },
          scale: { start: 0.5, end: 0 },
          alpha: { start: 0.8, end: 0 },
          lifespan: 400,
          quantity: 7,
          tint: color,
          blendMode: Phaser.BlendModes.ADD,
          emitting: false,
        });
        emitter.explode(7);
        this.scene.time.delayedCall(500, () => emitter.destroy());

        parts.delete(sprite);
        sprite.destroy();
      },
    });
  }

  /**
   * Force-cleanup any in-flight multi-orb staggered dispatch animation for this attacker.
   * Called when an attacker is removed from the game (exhausted / game-over) before
   * its entry animation has completed.  Destroys temp orb sprites, connector graphic,
   * stops pending tweens & delayed calls, and removes the per-frame update listener.
   */
  cleanupTransitionAnimation(attackerId: number): void {
    const data = this.transitionCleanup.get(attackerId);
    if (!data) return;

    // Stop all pending delayed calls (stagger timers that haven't fired yet)
    for (const dc of data.delayedCalls) {
      dc.remove(false);
    }

    // Stop all active tweens
    for (const tw of data.tweens) {
      if (tw && tw.isPlaying()) tw.stop();
    }

    // Remove per-frame connector redraw
    this.scene.events.off('update', data.updateHandler);

    // Destroy temp visuals
    data.connector.destroy();
    for (const s of data.orbSprites) {
      if (s && s.active) s.destroy();
    }

    // Remove from tracking maps
    this.transitionCleanup.delete(attackerId);
    this.transitioningAttackers.delete(attackerId);
  }

  /** Clean up all sprite maps and transition data. Called on scene shutdown. */
  destroy(): void {
    // Clean up any in-flight transition animations
    for (const attackerId of this.transitionCleanup.keys()) {
      this.cleanupTransitionAnimation(attackerId);
    }
    this.attackerSprites.clear();
    this.transitioningAttackers.clear();
    this.transitionCleanup.clear();
    this.containerParts.clear();
  }
}
